import React, { useState } from "react"
import { useLocation } from "react-router-dom"
import { useQuery, useQueryClient } from "react-query"
import axios, { AxiosError } from "axios"
import moment from "moment"
import { toast } from "react-toastify"
import { PageTitle } from "../../../../../_metronic/layout/core"
import { API_ESPACE_FOURNISSEUR_RESP_GET_DECLARATIONS_LISTING_ENDPOINT } from "../../../../constants"
import { IListingItemResponseModel, IListingResponseModel } from "../core/models"
import MyAlert from "../../../../utils/MyAlert"
import MyModal from "../../../../utils/MyModal"
import DeclarationsListNewItem from "./Declarationslistnewitem"
import { DeclarationCreate } from "../../../../app-consultant/modules/activites/components/declaration/declarationCreate/DeclarationCreate"
import { DeclarationUpdate } from "../../../../app-consultant/modules/activites/components/declaration/declarationUpdate/DeclarationUpdate"

// ─── Types ────────────────────────────────────────────────────────────────────

interface LocationState {
     mois?: string
     etat?: string
}

interface EditModalState {
     item: IListingItemResponseModel
     defaultStep: string
     createdDeclarationId?: number
}

// ─── Component ────────────────────────────────────────────────────────────────

const DeclarationsListNew = () => {
     const queryClient = useQueryClient()
     const location = useLocation() as { state: LocationState }
     const urlParams = new URLSearchParams(window.location.search)

     if (!location.state?.mois && !urlParams.get("mois")) throw new Error("Param mois is required")

     const mois = moment((location.state?.mois as string) || (urlParams.get("mois") as string))
     const etat = location.state?.etat || urlParams.get("etat")

     const [searchTerm, setSearchTerm] = useState("")
     const [expandedId, setExpandedId] = useState<number | null>(null)
     const [editModal, setEditModal] = useState<EditModalState | null>(null)

     // ── Query ──────────────────────────────────────────────────────────────────

     const listingQuery = useQuery<IListingResponseModel, AxiosError>(["QUERY_LISTING_NEW", mois.format("YYYY-MM")], () =>
          axios
               .post<IListingResponseModel>(API_ESPACE_FOURNISSEUR_RESP_GET_DECLARATIONS_LISTING_ENDPOINT, {
                    mois: location.state?.mois || urlParams.get("mois"),
                    etat,
               })
               .then(r => r.data)
               .catch((e: AxiosError) => {
                    toast.error((e.response?.data as any)?.detail, { autoClose: false })
                    throw e
               })
     )

     // ── Row refresh ───────────────────────────────────────────────────────────

     const refreshRow = async (consultantId: number) => {
          try {
               const { data: fresh } = await axios.post<IListingResponseModel>(API_ESPACE_FOURNISSEUR_RESP_GET_DECLARATIONS_LISTING_ENDPOINT, {
                    mois: location.state?.mois || urlParams.get("mois"),
                    consultants: [consultantId],
               })
               queryClient.setQueryData<IListingResponseModel | undefined>(["QUERY_LISTING_NEW", mois.format("YYYY-MM")], old => {
                    if (!old) return old
                    const updated = { ...old, data: [...old.data] }
                    const oldIdx = updated.data.findIndex(i => i.consultant.id === consultantId)
                    const freshIdx = fresh.data.findIndex(i => i.consultant.id === consultantId)
                    if (oldIdx >= 0 && freshIdx >= 0) {
                         updated.data[oldIdx] = { ...updated.data[oldIdx], ...fresh.data[freshIdx] }
                    }
                    return updated
               })
               setExpandedId(null)
          } catch {
               toast.error("Erreur lors du rechargement.", { autoClose: false })
          }
     }

     // ── Edit modal handlers ───────────────────────────────────────────────────

     const handleOpenEdit = (item: IListingItemResponseModel, defaultStep: string) => {
          setEditModal({ item, defaultStep })
     }

     const handleCloseEdit = () => {
          if (editModal) {
               refreshRow(editModal.item.consultant.id!)
          }
          setEditModal(null)
     }

     const handleDeclarationCreated = (declarationId: number) => {
          if (editModal) {
               setEditModal({ ...editModal, createdDeclarationId: declarationId, defaultStep: "recap" })
          }
     }

     // ── Loading ────────────────────────────────────────────────────────────────

     if (listingQuery.isFetching) {
          return (
               <div className="text-center py-20">
                    <span className="spinner-border text-primary mb-4" style={{ width: "2.5rem", height: "2.5rem" }} />
                    <p className="text-gray-500 fw-semibold">Chargement des déclarations...</p>
               </div>
          )
     }

     if (listingQuery.isError) {
          return (
               <MyAlert type="danger" classNames="mb-4">
                    <>
                         {(listingQuery.error.response?.data as any)?.detail}
                         <button className="btn btn-sm btn-danger ms-2" onClick={() => listingQuery.refetch()}>
                              Recharger
                         </button>
                    </>
               </MyAlert>
          )
     }

     // ── Filtered list ──────────────────────────────────────────────────────────

     const filtered = (listingQuery.data?.data || []).filter(item => item.consultant?.prenomNom?.toLowerCase().includes(searchTerm.toLowerCase()))

     const total = listingQuery.data?.data.length || 0
     const pending = (listingQuery.data?.data || []).filter(i => !i.declaration || !i.declaration.etat).length

     // ── Modal content ──────────────────────────────────────────────────────────

     const showCreate = editModal && !editModal.item.declaration && !editModal.createdDeclarationId
     const showUpdate = editModal && (editModal.item.declaration || editModal.createdDeclarationId)
     const declarationIdForUpdate = editModal?.createdDeclarationId || editModal?.item.declaration?.id

     return (
          <>
               <PageTitle breadcrumbs={[{ title: "Déclarations", path: "/espace-resp-fournisseur/declarations/voyants" }, { title: mois.format("MMMM YYYY") }]}>
                    Déclarations
               </PageTitle>

               <div className="d-flex flex-column gap-6">
                    {/* ── Summary banner ─────────────────────────────────────── */}
                    <div className="row g-4">
                         <StatCard icon="fas fa-users" color="primary" label="Collaborateurs" value={total} />
                         <StatCard icon="fas fa-hourglass-half" color="warning" label="En attente" value={pending} />
                         <StatCard icon="fas fa-check-circle" color="success" label="Traitées" value={total - pending} />
                    </div>

                    {/* ── Search ─────────────────────────────────────────────── */}
                    <div className="d-flex align-items-center gap-3">
                         <div className="position-relative flex-grow-1" style={{ maxWidth: 360 }}>
                              <i className="fas fa-search position-absolute text-gray-400" style={{ top: "50%", left: "1rem", transform: "translateY(-50%)" }} />
                              <input
                                   type="text"
                                   className="form-control ps-10"
                                   placeholder="Rechercher un collaborateur..."
                                   value={searchTerm}
                                   onChange={e => setSearchTerm(e.target.value)}
                                   autoComplete="off"
                              />
                         </div>
                         <span className="text-gray-400 fs-7 fw-semibold">
                              {filtered.length} résultat{filtered.length !== 1 ? "s" : ""}
                         </span>
                    </div>

                    {/* ── List ───────────────────────────────────────────────── */}
                    {filtered.length === 0 ? (
                         <div className="text-center py-16 text-gray-400">
                              <i className="fas fa-inbox fs-3x mb-4 d-block" />
                              <span className="fw-semibold">Aucun collaborateur trouvé</span>
                         </div>
                    ) : (
                         <div className="d-flex flex-column gap-3">
                              {filtered.map(item => (
                                   <DeclarationsListNewItem
                                        key={item.consultant.id}
                                        item={item}
                                        mois={mois}
                                        isExpanded={expandedId === item.consultant.id}
                                        onToggle={() => setExpandedId(prev => (prev === item.consultant.id ? null : item.consultant.id!))}
                                        onSuccess={() => refreshRow(item.consultant.id!)}
                                        onEdit={defaultStep => handleOpenEdit(item, defaultStep)}
                                   />
                              ))}
                         </div>
                    )}
               </div>

               {/* ── Edit modal ──────────────────────────────────────────────── */}
               {editModal && (
                    <MyModal title={<>{editModal.item.consultant.prenomNom}</>} show={true} handleClose={handleCloseEdit} fullscreen={true}>
                         {showCreate && (
                              <DeclarationCreate
                                   consultant_ID={editModal.item.consultant.id!}
                                   onSubmitSuccessForCreationCallback={handleDeclarationCreated}
                                   onSubmitDeclarationExistsCallback={() => setEditModal(prev => (prev ? { ...prev, defaultStep: "recap" } : null))}
                                   onClickPreviousCallback={handleCloseEdit}
                              />
                         )}
                         {showUpdate && declarationIdForUpdate && (
                              <DeclarationUpdate
                                   defaultStep={editModal.defaultStep as any}
                                   declarationId={declarationIdForUpdate}
                                   onClickOnRetourAuxDeclarationCallback={handleCloseEdit}
                                   onChangeNbJoursTravaillesCallback={() => {}}
                                   onCRUDFacturesPrestationCallback={() => refreshRow(editModal.item.consultant.id!)}
                                   onCRUDFacturesFraisCallback={() => refreshRow(editModal.item.consultant.id!)}
                              />
                         )}
                    </MyModal>
               )}
          </>
     )
}

// ─── Stat card helper ─────────────────────────────────────────────────────────

const StatCard = ({ icon, color, label, value }: { icon: string; color: string; label: string; value: number }) => (
     <div className="col-4">
          <div className={`card shadow-sm border-0 bg-light-${color}`}>
               <div className="card-body py-5 px-6 d-flex align-items-center gap-4">
                    <div className={`symbol symbol-45px`}>
                         <div className={`symbol-label bg-${color}`}>
                              <i className={`${icon} text-white fs-3`} />
                         </div>
                    </div>
                    <div>
                         <span className={`fw-bold fs-2 text-${color} d-block`}>{value}</span>
                         <span className="text-gray-600 fw-semibold fs-8">{label}</span>
                    </div>
               </div>
          </div>
     </div>
)

export default DeclarationsListNew
